/**
 * Public API for Sanskrit nominal declension.
 *
 * Forms are derived by vidyut-prakriya, an implementation of some 2,000 rules of
 * Pāṇini's Aṣṭādhyāyī, run in WebAssembly in the browser. Every form comes with
 * its *prakriyā* — the ordered list of sūtras that produced it — so the UI can
 * show not just what a form is but why.
 *
 * This replaces a hand-written table of regex rules. The table could not express
 * the strong/middle/weak stem grades that generate most of Sanskrit's apparent
 * irregularity, so each exception had to be written out by hand and the
 * consonant stems were largely wrong.
 */

import { CaseName, Gender, Num } from "./types";
import { annotate, LexicalHints, mergeHints, parseMwHints, Confidence } from "./annotation";
import {
    CASE_ORDER,
    NUM_ORDER,
    DerivedForm,
    Paradigm,
    PrakriyaStep,
    declineParadigm,
    loadEngine,
    normalizeIast,
} from "./vidyut-engine";

export { CASE_ORDER, NUM_ORDER, parseMwHints, mergeHints, loadEngine, normalizeIast };
export type { DerivedForm, Paradigm, PrakriyaStep, LexicalHints, Confidence };

export { analyze, reverseDeclension } from "./analyze";
export type { Analysis, DeclensionWord } from "./analyze";

/* --------------------------------------------------- word types (unchanged) */
/* Consumed by the Sanskrit Heritage stemmer in `stemmer.tsx`, which is a
   separate, remote lookup and is untouched by this rewrite. */

export type Word = {
    word: string;
    type: "nounOrAdjective" | "verb" | "indeclinableVerb" | "indeclianbleParticiple" | "generic" | "indeclinable";
};

export type NounOrAdjectiveDeclensionWord = Word & { gen: string; num: string; case: string; stem: string };
export type VerbDeclensionWord = Word & { word: string; description: string; root: string };
export type IndeclinableParticibleWord = Word & { description: string; stem: string };
export type IndeclinableVerbWord = Word & { description: string; root: string };
export type GenericTypeWord = Word & { unknownType: string; description: string };
export type IndeclinableWord = Word;

/* ------------------------------------------------------------- declining */

/** A full paradigm plus how confident we are in the analysis behind it. */
export type DeclensionResult = Paradigm & {
    /** `attested` when the analysis is recorded, `inferred` when read off the word's shape. */
    confidence: Confidence;
    /** Gloss of the analysis used, e.g. "√kṛ + tṛc (agent noun)". */
    note?: string;
    /** Which paradigm the stem was treated as belonging to. */
    stemClass?: string;
};

/**
 * Declines `stem` in `gender`.
 *
 * `hints` carries whatever the dictionary entry says about the word — its gender,
 * how it forms its feminine, whether it is a verbal root. Pass
 * `parseMwHints(entryHtml)` to get them from a Monier-Williams entry.
 */
export const decline = async (
    stem: string,
    gender: Gender,
    hints: LexicalHints = {},
): Promise<DeclensionResult> => {
    const annotated = annotate(stem, gender, hints);
    const paradigm = await declineParadigm(annotated.analysis, gender);
    return { ...paradigm, confidence: annotated.confidence, note: annotated.note };
};

/**
 * Which genders a word can be declined in, given what the dictionary says.
 * Falls back to all three when the entry records nothing.
 */
export const availableGenders = (hints: LexicalHints = {}): Gender[] =>
    hints.genders && hints.genders.length > 0 ? hints.genders : ["mas", "fem", "neu"];

/** Flattens a paradigm cell to display text, joining genuine variants with a slash. */
export const cellText = (forms: DerivedForm[]): string =>
    forms.length === 0 ? "—" : forms.map(f => f.text).join(" / ");

export type { CaseName, Gender, Num };
